import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Mail, Lock, User, Phone, Home, Loader2, CheckCircle2, LogOut, Receipt, ChevronRight, Smartphone, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { BrandLogo } from '../components/BrandLogo';
import { useCMS } from '../context/CMSContext';
import api from '../api';
import SignInBackground from '../components/SignInBackground';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { readLocal, writeLocal } from '../lib/storage';

const AccountPage = () => {
  const { customer, loginCustomer, registerCustomer, logoutCustomer } = useAuth();
  const { t } = useCMS();
  const [tab, setTab] = useState('signin');

  if (customer) return <CustomerDashboard customer={customer} onLogout={logoutCustomer} />;

  return (
    <SignInBackground>
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-7">
          <BrandLogo name={t('brand.name', 'FAT BUSTERS')} variant="dark" />
          <p className="mt-2 text-[11px] uppercase tracking-[0.36em] text-red-500/95 font-semibold">by PULSE</p>
          <p className="mt-3 text-xs uppercase tracking-[0.4em] text-red-400/90">Members area</p>
        </div>

        <div className="fb-signin-card rounded-2xl overflow-hidden">
          <div className="flex border-b border-red-500/15">
            <TabBtn active={tab === 'signin'} onClick={() => setTab('signin')}>Sign in</TabBtn>
            <TabBtn active={tab === 'register'} onClick={() => setTab('register')}>Create account</TabBtn>
          </div>
          <div className="p-6 sm:p-8">
            {tab === 'signin' ? <SignInForm onLogin={loginCustomer} switchToRegister={() => setTab('register')} /> : <RegisterForm onRegister={registerCustomer} switchToSignIn={() => setTab('signin')} />}
          </div>
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-white/50 hover:text-red-400 transition-colors">← Back to shop</Link>
        </div>
      </div>
    </SignInBackground>
  );
};

const TabBtn = ({ active, onClick, children }) => (
  <button type="button" onClick={onClick}
    className={`flex-1 py-4 text-sm font-semibold tracking-wide transition-colors relative ${
      active ? 'text-red-400' : 'text-white/50 hover:text-white/80'
    }`}>
    {children}
    {active && <span className="absolute left-1/2 -translate-x-1/2 bottom-0 h-[2px] w-12 bg-red-500 shadow-[0_0_10px_rgba(239,68,68,.6)]" />}
  </button>
);

// ---------- Sign in ----------
// We remember the last customer email used on this device so returning users
// only need to type their password. The cookie is httpOnly so we can't read
// the session itself — this is just a UX nicety in client storage.
const LAST_EMAIL_KEY = 'fb_last_customer_email';

// Sign-in submission helpers — pure functions extracted from the form body
// to keep the SignInForm component's cyclomatic complexity in single digits.

const handleVerificationRedirect = (detail, email, nav, location) => {
  writeLocal(LAST_EMAIL_KEY, email);
  toast.message('Verify your phone — code sent');
  nav('/verify-phone', { state: { email: detail.email, phone: detail.phone, redirectTo: location.state?.from || '/' } });
};

const extractSignInError = (ex) => {
  const detail = ex?.response?.data?.detail;
  if (detail && typeof detail === 'object' && detail.requires_verification) return { kind: 'verify', detail };
  return { kind: 'message', message: typeof detail === 'string' ? detail : 'Sign in failed' };
};

const performSignIn = async ({ email, password, onLogin, nav, location, setErr }) => {
  try {
    await onLogin(email, password);
    writeLocal(LAST_EMAIL_KEY, email);
    toast.success('Welcome back!');
    nav(location.state?.from || '/');
  } catch (ex) {
    const result = extractSignInError(ex);
    if (result.kind === 'verify') return handleVerificationRedirect(result.detail, email, nav, location);
    setErr(result.message);
  }
};

const SignInForm = ({ onLogin, switchToRegister }) => {
  const nav = useNavigate();
  const location = useLocation();
  const { t } = useCMS();
  const [email, setEmail] = useState(() => readLocal(LAST_EMAIL_KEY, '') || '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try { await performSignIn({ email, password, onLogin, nav, location, setErr }); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <h2 className="text-2xl font-bold text-white">Welcome back</h2>
      <p className="text-sm text-white/55">Sign in to your account.</p>
      <Field icon={Mail} label="Email" type="email" value={email} onChange={setEmail} required />
      <Field icon={Lock} label="Password" type="password" value={password} onChange={setPassword} required />
      {err && <div className="text-sm text-red-300 bg-red-900/30 border border-red-500/30 rounded-lg px-3 py-2">{err}</div>}
      <button type="submit" disabled={busy}
        className="w-full py-3 mt-2 bg-red-500 hover:bg-red-400 disabled:opacity-60 text-white font-semibold rounded-lg flex items-center justify-center gap-2 shadow-lg shadow-red-500/30 transition-colors">
        {busy && <Loader2 size={16} className="animate-spin" />} Sign in
      </button>
      <p className="text-sm text-white/55 text-center">
        New to <span className="font-semibold text-white">{t('brand.name', 'FAT BUSTERS')}</span>?{' '}
        <button type="button" onClick={switchToRegister} className="text-red-400 hover:text-red-300 font-semibold">Create an account</button>
      </p>
    </form>
  );
};

// ---------- Register ----------
const handleRegisterResult = (result, location, nav) => {
  if (result?.requires_verification) {
    toast.message('Code sent — check your phone');
    nav('/verify-phone', { state: { email: result.email, phone: result.phone, redirectTo: location.state?.from || '/' } });
    return true;
  }
  toast.success('Account created — welcome!');
  nav(location.state?.from || '/');
  return false;
};

const RegisterForm = ({ onRegister, switchToSignIn }) => {
  const nav = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({
    full_name: '', email: '', password: '', phone: '', address: '', is_18_plus: false,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === 'string' ? v : v.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!form.is_18_plus) {
      setErr('You must confirm you are 18 or older to create an account.');
      return;
    }
    setBusy(true);
    try {
      const result = await onRegister(form);
      handleRegisterResult(result, location, nav);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Registration failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <h2 className="text-2xl font-bold text-white">Create your account</h2>
      <p className="text-sm text-white/55">Quick — takes about a minute.</p>

      <Field icon={User}  label="Full Name"      type="text"     value={form.full_name} onChange={set('full_name')} required />
      <Field icon={Mail}  label="Email"          type="email"    value={form.email}     onChange={set('email')} required />
      <Field icon={Phone} label="Phone"          type="tel"      value={form.phone}     onChange={set('phone')} required />
      <p className="-mt-3 text-[11px] text-red-300/80 flex items-center gap-1.5 leading-snug">
        <Smartphone size={11} />
        We'll text a 6-digit code to verify your phone before activating your account.
      </p>
      <RegisterAddressField value={form.address} onChange={set('address')} />
      <Field icon={Lock}  label="Password (min 6 chars)" type="password" value={form.password} onChange={set('password')} required minLength={6} />

      <AgeGateCheckbox checked={form.is_18_plus} onChange={(checked) => setForm({ ...form, is_18_plus: checked })} />

      {err && <div className="text-sm text-red-300 bg-red-900/30 border border-red-500/30 rounded-lg px-3 py-2">{err}</div>}

      <button type="submit" disabled={busy}
        className="w-full py-3 bg-red-500 hover:bg-red-400 disabled:opacity-60 text-white font-semibold rounded-lg flex items-center justify-center gap-2 shadow-lg shadow-red-500/30 transition-colors">
        {busy && <Loader2 size={16} className="animate-spin" />} Send verification code
      </button>
      <p className="text-[11px] text-white/40 text-center">
        Standard SMS rates may apply on your carrier.
      </p>

      <p className="text-sm text-white/55 text-center">
        Already have an account?{' '}
        <button type="button" onClick={switchToSignIn} className="text-red-400 hover:text-red-300 font-semibold">Sign in</button>
      </p>
    </form>
  );
};

const RegisterAddressField = ({ value, onChange }) => (
  <label className="block">
    <span className="text-[11px] font-semibold tracking-[0.14em] uppercase text-white/55">Address</span>
    <div className="mt-1.5">
      <AddressAutocomplete value={value} onChange={onChange} required variant="dark" testid="register-address" />
    </div>
  </label>
);

const AgeGateCheckbox = ({ checked, onChange }) => (
  <label className="flex items-start gap-3 p-3 bg-red-500/10 border border-red-500/25 rounded-lg cursor-pointer">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-0.5 w-4 h-4 accent-red-500"
      required
    />
    <span className="text-sm text-white/80">
      I confirm I am <b className="text-white">18 years of age or older</b> and agree to the Terms of Service and Privacy Policy.
    </span>
  </label>
);

// ---------- Field ----------
const Field = ({ icon: Icon, label, type = 'text', value, onChange, required, textarea, minLength }) => (
  <label className="block">
    <span className="text-[11px] font-semibold tracking-[0.14em] uppercase text-white/55">{label}</span>
    <div className="relative mt-1.5">
      {Icon && <Icon size={16} className="absolute left-3.5 top-3.5 text-red-400/70 pointer-events-none" />}
      {textarea ? (
        <textarea required={required} rows={2} value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full ${Icon ? 'pl-10' : 'pl-3.5'} pr-3 py-3 bg-black/40 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-red-400/50 transition-colors resize-none`} />
      ) : (
        <input required={required} type={type} value={value} minLength={minLength}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full ${Icon ? 'pl-10' : 'pl-3.5'} pr-3 py-3 bg-black/40 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-red-400/50 transition-colors`} />
      )}
    </div>
  </label>
);

// ---------- Customer dashboard ----------
const CustomerDashboard = ({ customer, onLogout }) => (
  <main className="max-w-3xl mx-auto px-6 py-12">
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <div>
        <p className="text-sm text-slate-500">Account</p>
        <h1 className="text-3xl font-bold text-[#0a0a0a]">Welcome, {customer.full_name?.split(' ')[0] || 'there'}</h1>
      </div>
      <button onClick={onLogout}
        className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-lg">
        <LogOut size={14} /> Sign out
      </button>
    </div>

    <MyOrders />

    <h2 className="text-lg font-bold text-[#0a0a0a] mt-10 mb-3">Your details</h2>
    <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
      <Row label="Full name" value={customer.full_name} />
      <Row label="Email" value={customer.email} />
      <Row label="Phone" value={customer.phone || '—'} />
      <Row label="Address" value={customer.address || '—'} />
      <Row label="Age verified" value={<span className="inline-flex items-center gap-1 text-teal-700"><CheckCircle2 size={14} /> 18+ confirmed</span>} />
    </div>

    <p className="mt-6 text-xs text-slate-400">Need to update your details? <Link to="/contact" className="text-red-600 hover:text-red-500">Contact us</Link>.</p>
  </main>
);

// ---------- My Orders ----------
const STATUS_BADGE = {
  pending:   'bg-amber-50 text-amber-700 border-amber-200',
  paid:      'bg-emerald-50 text-emerald-700 border-emerald-200',
  shipped:   'bg-sky-50 text-sky-700 border-sky-200',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
};

// External effect bodies — keep the hook one-liners.
const fetchMyOrders = (mountedRef, setOrders, setError) => {
  api.get('/orders/me')
    .then((res) => { if (mountedRef.current) setOrders(res.data); })
    .catch(() => { if (mountedRef.current) setError('Could not load orders'); });
};

const reorderItems = (order, addItem, setIsOpen, navigate) => {
  if (!order.items?.length) return;
  order.items.forEach((it) => {
    addItem({ id: it.product_id, name: it.name, price: it.price, image: it.image || '' }, it.qty);
  });
  setIsOpen(false);
  toast.success(`${order.items.length} item${order.items.length === 1 ? '' : 's'} added to your cart`);
  navigate('/checkout');
};

// --- Sub-components for MyOrders ---

const MyOrdersHeader = ({ orders }) => (
  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
    <h2 className="text-lg font-bold text-[#0a0a0a] inline-flex items-center gap-2">
      <Receipt size={18} className="text-red-600" /> My orders
    </h2>
    {orders && orders.length > 0 && (
      <span className="text-xs text-slate-500">{orders.length} order{orders.length === 1 ? '' : 's'}</span>
    )}
  </div>
);

const OrdersEmptyState = () => (
  <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
    <p className="text-sm text-slate-500">No orders yet.</p>
    <Link to="/" className="inline-block mt-3 px-4 py-2 bg-red-500 hover:bg-red-400 text-white text-sm font-semibold rounded-lg" data-testid="my-orders-shop-btn">
      Browse products
    </Link>
  </div>
);

const OrderRow = ({ order, onReorder }) => (
  <Link
    to={`/pay/${order.reference}`}
    className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors"
    data-testid={`my-order-${order.reference}`}
  >
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono font-bold text-[#0a0a0a]">{order.reference}</span>
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border capitalize ${STATUS_BADGE[order.status] || STATUS_BADGE.pending}`}>
          {order.status}
        </span>
      </div>
      <p className="text-xs text-slate-500 mt-0.5 truncate">
        {order.items.length} item{order.items.length === 1 ? '' : 's'} · {new Date(order.created_at).toLocaleDateString('en-AU', { dateStyle: 'medium' })}
      </p>
    </div>
    {order.status !== 'pending' && (
      <button
        type="button"
        onClick={onReorder}
        data-testid={`reorder-${order.reference}`}
        className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-red-600 hover:text-white hover:bg-red-500 border border-red-200 hover:border-red-500 rounded-lg transition-colors"
        title="Add these items to your cart again"
      >
        <RotateCcw size={12} /> Reorder
      </button>
    )}
    <OrderRowTrailing order={order} onReorder={onReorder} />
  </Link>
);

const OrderRowTrailing = ({ order, onReorder }) => (
  <div className="text-right">
    <div className="font-semibold text-[#0a0a0a]">A${order.total.toFixed(2)}</div>
    {order.status === 'pending' ? (
      <span className="text-[11px] text-red-600 font-semibold inline-flex items-center gap-0.5">Pay now <ChevronRight size={12} /></span>
    ) : (
      <button
        type="button"
        onClick={onReorder}
        data-testid={`reorder-mobile-${order.reference}`}
        className="sm:hidden mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 hover:text-red-500"
      >
        <RotateCcw size={11} /> Reorder
      </button>
    )}
  </div>
);

const MyOrders = () => {
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState('');
  const { addItem, setIsOpen } = useCart();
  const navigate = useNavigate();
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    fetchMyOrders(mountedRef, setOrders, setError);
    return () => { mountedRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setOrders, setError, mountedRef, fetchMyOrders]);

  const handleReorder = (order) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    reorderItems(order, addItem, setIsOpen, navigate);
  };

  return (
    <section className="mt-10" data-testid="my-orders-section">
      <MyOrdersHeader orders={orders} />
      {orders === null && !error && (
        <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
          <Loader2 size={20} className="animate-spin text-red-500 mx-auto" />
        </div>
      )}
      {error && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 text-sm text-slate-500 text-center">{error}</div>
      )}
      {orders && orders.length === 0 && <OrdersEmptyState />}
      {orders && orders.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden divide-y divide-slate-100">
          {orders.map((o) => (
            <OrderRow key={o.id} order={o} onReorder={handleReorder(o)} />
          ))}
        </div>
      )}
    </section>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-4 px-5 py-4">
    <span className="text-xs font-semibold tracking-[0.1em] uppercase text-slate-500">{label}</span>
    <span className="text-sm text-[#0a0a0a] font-medium text-right">{value}</span>
  </div>
);

export default AccountPage;
